import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatDate, imageUrl } from "@/lib/api";
import { ArticleMeta } from "@/components/site/Meta";
import NotFound from "@/pages/NotFound";

export function SeriesIndex() {
  const [series, setSeries] = useState(null);
  useEffect(() => { api.get("/series-index").then((r) => setSeries(r.data)); }, []);
  return (
    <div className="max-w-3xl pt-14" data-testid="series-index-page">
      <p className="meta">Series</p>
      <h1 className="mt-3 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight">Investigations in parts</h1>
      <p className="mt-4 text-[var(--ink-2)] text-lg">Longer stories, told over several pieces. Start at part one.</p>
      <div className="mt-8 border-t border-[var(--ink)]" data-testid="series-index-list">
        {series?.length === 0 && <p className="py-12 italic text-[var(--ink-3)]" data-testid="series-index-empty">No series published yet.</p>}
        {series?.map((s, i) => (
          <Link key={s.slug} to={`/series/${s.slug}`} className="group fade-up grid gap-2 py-8 border-b border-[var(--rule)]" style={{ animationDelay: `${100 + i * 70}ms` }} data-testid={`series-card-${s.slug}`}>
            <span className="meta">{s.count} {s.count === 1 ? "part" : "parts"} · Latest {formatDate(s.latest)}</span>
            <span className="font-display text-3xl font-semibold italic group-hover:text-[var(--accent)] transition-colors">{s.name}</span>
            {s.standfirst && <span className="text-[var(--ink-2)] leading-relaxed">{s.standfirst}</span>}
          </Link>
        ))}
      </div>
    </div>
  );
}

export function SeriesPage() {
  const { slug } = useParams();
  const [state, setState] = useState({ loading: true });
  useEffect(() => {
    api.get(`/series/${slug}`).then((r) => setState({ data: r.data })).catch(() => setState({ notFound: true }));
  }, [slug]);
  if (state.loading) return <p className="py-24 meta">Loading</p>;
  if (state.notFound) return <NotFound />;
  const s = state.data;

  return (
    <div className="max-w-3xl pt-14" data-testid="series-page">
      <Link to="/series" className="meta link-quiet" data-testid="series-back-link">All series</Link>
      <h1 className="mt-4 font-display text-4xl sm:text-5xl lg:text-6xl font-bold italic tracking-tight leading-[1.05]" data-testid="series-title">{s.name}</h1>
      <p className="mt-5 meta" data-testid="series-part-count">{s.count} {s.count === 1 ? "part" : "parts"}</p>
      {s.image_path && <img src={imageUrl(s.image_path)} alt="" className="mt-8 w-full aspect-[16/7] object-cover" />}
      <ol className="mt-10 border-l-2 border-[var(--accent)]">
        {s.articles.map((a, i) => (
          <li key={a.id} className="relative pl-8 pb-10 last:pb-0" data-testid={`series-page-part-${a.slug}`}>
            <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-[var(--paper)] border-2 border-[var(--accent)]" />
            <p className="meta !text-[var(--accent)]">Part {a.series_part || i + 1}</p>
            <h2 className="mt-2 font-display text-2xl sm:text-3xl font-semibold leading-snug">
              <Link to={`/article/${a.slug}`} className="headline-link">{a.headline}</Link>
            </h2>
            {a.standfirst && <p className="mt-2 text-[var(--ink-2)] leading-relaxed">{a.standfirst}</p>}
            <div className="mt-3"><ArticleMeta article={a} idPrefix={`series-page-${a.slug}`} /></div>
          </li>
        ))}
      </ol>
    </div>
  );
}
